import { useContext, useEffect, useState } from "react";
import { usePayment } from "../../../Account/BillingInformation/Shared/hooks/Payment/usePayment";
import { usePaymentMethodFetch } from "../../../Account/BillingInformation/Shared/hooks/PaymentMethodFetch/usePaymentMethodFetch";
import { useBillingInformation } from "../../../Account/BillingInformation/Shared/hooks/useBillingInformation/useBillingInformation";
import useToast from "../../../hooks/useToast";
import { AdjustmentApplication, AdjustmentType, AdjustmentValidation } from "../../../MealTrack/MTAPITypes";
import { calculateDayBank } from "../OrderSummary/OrdersTotalsSummary";
import { getOrderAdjustments } from "../OrderSummary/orderSummary-utils";
import { useCartSettings } from "../OrderSummary/OrderSummary.request";
import { processNewGiftCard } from "./PaymentForm-utils";
import { PaymentFormContext } from "./PaymentFormContext";
import { usePaymentMethodCreate } from "../../../Account/BillingInformation/Shared/hooks/PaymentMethodCreate/usePaymentMethodCreate";
export const usePaymentForm = (isFormDisabled = false) => {
    var _a, _b, _c;
    const [showAddGiftCardTextBox, setShowAddGiftCardTextBox] = useState(false);
    const { customerCreditCards, customerGiftCards, usingStripe, publicKey, isTapCardFormOpen, setIsTapCardFormOpen, isStripeCardFormOpen, setIsStripeCardFormOpen, token, authorizeUrl, clientSecret, stripePromise, options, setStripePromise, stripePK, selectedCard, setSelectedCard, enableGiftCards, setCustomerGiftCards, paymentMethodUpdate, } = useBillingInformation();
    const { balance, setBalance, setIsFormSubmitted, resetContext, isFormSubmitted, giftCard, setGiftCard, paymentMethod, setPaymentMethod, } = useContext(PaymentFormContext);
    const { loading: initPaymentLoading, doesPaymentIntentExist, updatePayInitiation, initPayWithPaymentMethod, } = usePayment();
    const { initPaymentMethodCreation, loading: initCreateLoading } = usePaymentMethodCreate();
    const toast = useToast();
    const { getGiftCardByNumber } = usePaymentMethodFetch();
    const { settings } = useCartSettings("");
    const primaryCard = customerCreditCards === null || customerCreditCards === void 0 ? void 0 : customerCreditCards.find((card) => card.isPrimary);
    const defaultCard = !!primaryCard ? primaryCard : (customerCreditCards === null || customerCreditCards === void 0 ? void 0 : customerCreditCards.length) ? customerCreditCards[0] : undefined;
    const remainingBalance = (_a = balance === null || balance === void 0 ? void 0 : balance.remainingBalance) !== null && _a !== void 0 ? _a : 0;
    const giftCardDeduction = giftCard ? -Math.min(giftCard.balance, remainingBalance) : 0;
    const amountToBePaid = remainingBalance + giftCardDeduction;
    const totalAppliedPayments = (_b = balance === null || balance === void 0 ? void 0 : balance.appliedPayments.reduce((acc, payment) => { var _a; return acc + ((_a = payment.paymentAmount) !== null && _a !== void 0 ? _a : 0); }, 0)) !== null && _b !== void 0 ? _b : 0;
    const { dayBankQuantity, dayBankValue } = calculateDayBank(balance);
    const taxRate = balance === null || balance === void 0 ? void 0 : balance.cost.order.taxRate;
    const adjustments = getOrderAdjustments(balance);
    const perMealCoupon = balance === null || balance === void 0 ? void 0 : balance.cost.order.adjustments.find((c) => c.application === AdjustmentApplication.PerMeal &&
        !(c.adjustmentType === AdjustmentType.Surcharge) &&
        c.validation === AdjustmentValidation.Valid);
    const perMealCouponDiscountAppliedValue = perMealCoupon ? perMealCoupon.appliedValue : 0;
    const subtotalMinusPerMealCoupon = balance ? balance.cost.order.subtotal - perMealCouponDiscountAppliedValue : 0;
    const subTotal = settings && settings.discountPerMeal.display ? subtotalMinusPerMealCoupon : (_c = balance === null || balance === void 0 ? void 0 : balance.cost.order.subtotal) !== null && _c !== void 0 ? _c : 0;
    useEffect(() => {
        setPaymentMethod(defaultCard);
        setGiftCard(undefined);
    }, [isFormSubmitted]);
    useEffect(() => {
        if (defaultCard && !paymentMethod)
            setPaymentMethod(defaultCard);
    }, [defaultCard]);
    useEffect(() => {
        // Don't initiate or update payment if the Stripe form is open
        // This avoids generating a payment intent client secret when we don't want one
        if (isStripeCardFormOpen)
            return;
        if (!!paymentMethod && amountToBePaid > 0 && !isFormSubmitted && !isFormDisabled) {
            if (doesPaymentIntentExist) {
                updatePayInitiation(amountToBePaid);
            }
            else {
                initPayWithPaymentMethod({ PaymentMethodId: paymentMethod.id, Amount: amountToBePaid });
            }
        }
    }, [paymentMethod, balance, amountToBePaid, isFormSubmitted, isFormDisabled, isStripeCardFormOpen]);
    return {
        settings,
        primaryCard,
        setIsFormSubmitted,
        amountToBePaid,
        giftCardDeduction,
        totalAppliedPayments,
        dayBankQuantity,
        dayBankValue,
        adjustments,
        taxRate,
        balance,
        subTotal,
        setBalance,
        resetContext,
        enableGiftCards,
        customerGiftCards,
        customerCreditCards,
        giftCard,
        setGiftCard,
        paymentMethod,
        setPaymentMethod,
        processNewGiftCard: processNewGiftCard(getGiftCardByNumber, giftCard, setGiftCard, customerGiftCards, setCustomerGiftCards, toast),
        usingStripe,
        publicKey,
        isTapCardFormOpen,
        setIsTapCardFormOpen,
        isStripeCardFormOpen,
        setIsStripeCardFormOpen,
        token,
        authorizeUrl,
        clientSecret,
        stripePromise,
        options,
        setStripePromise,
        stripePK,
        selectedCard,
        setSelectedCard,
        paymentMethodUpdate,
        setShowAddGiftCardTextBox,
        showAddGiftCardTextBox,
        initPaymentLoading,
        initPaymentMethodCreation,
        initCreateLoading,
    };
};
